import { first } from 'lodash';
import { useInfiniteQuery } from 'react-query';
import { queryGet } from '@pocapan/shared/utils/queryClient';

const DEFAULT_PAGE = 15;

const CATEGORY_MAP = {
  DM: ['dm'],
  Trading: ['selling', 'purchasing'],
  Exchanging: ['exchanging']
};

const BASE_KEY_MAP = {
  All: 'AllChatRooms',
  DM: 'DMChatRooms',
  Trading: 'TradingChatRooms',
  Exchanging: 'ExchangingChatRooms'
};

export default function useChatRoomsQuery(
  {
    type = 'All',
    tradingPhotoCardId = undefined,
    onlyJoined = false,
    onlyMine = false,
    perPage = DEFAULT_PAGE,
    categories: categoriesOverride,
    categoryFilterType
  },
  queryOptions = {}
) {
  const filters = {
    only_joined: onlyJoined,
    only_mine: onlyMine,
    trading_photo_card_id: tradingPhotoCardId,
    categories: categoriesOverride ?? CATEGORY_MAP[type],
    category_filter_type: categoryFilterType
  };

  return useChatRoomsQueryCore({
    baseKey: BASE_KEY_MAP[type] || BASE_KEY_MAP.All,
    baseUrl: '/api/v1/chat_rooms',
    filters,
    perPage,
    enabled: tradingPhotoCardId !== undefined ? !!tradingPhotoCardId : true,
    queryOptions
  });
}

function useChatRoomsQueryCore({
  baseKey,
  baseUrl,
  filters = {},
  perPage,
  enabled,
  queryOptions = {}
}) {
  const queryParams = createFilteredQueryParams({ perPage, filters });
  const cacheKey = generateCacheKey(baseKey, filters);

  const { data, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery(
    cacheKey,
    ({ pageParam = 1 }) => fetchChatRoomsQuery({ pageParam, url: baseUrl, queryParams }),
    {
      enabled,
      getNextPageParam: (lastPage) => lastPage?.meta?.pagination?.nextPage || undefined,
      staleTime: 1000 * 5,
      cacheTime: 1000 * 10,
      ...queryOptions
    }
  );

  return { data, isFetching, fetchNextPage, hasNextPage };
}

function createFilteredQueryParams({ perPage = DEFAULT_PAGE, page = 1, filters = {} }) {
  const queryParams = new URLSearchParams({ per_page: perPage, page });

  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== false) {
      if (Array.isArray(value)) {
        for (const v of value) queryParams.append(`${key}[]`, v);
      } else {
        queryParams.append(key, value);
      }
    }
  });

  return queryParams;
}

function generateCacheKey(baseKey, filters) {
  const filterKey = Object.entries(filters)
    .filter(([_, value]) => value !== undefined && value !== false)
    .map(([key, value]) => (Array.isArray(value) ? `${key}:${value.join(',')}` : `${key}:${value}`))
    .join('&');

  return [baseKey, filterKey];
}

async function fetchChatRoomsQuery({ pageParam = 1, url, queryParams }) {
  queryParams.set('page', pageParam);
  const finalUrl = `${url}?${queryParams.toString()}`;

  try {
    const data = await queryGet(finalUrl);
    return data;
  } catch (_error) {
    throw new Error('Error fetching paginated data.');
  }
}

export async function fetchRecentChatRoomQuery({ tradingPhotoCardId }) {
  const filters = { trading_photo_card_id: tradingPhotoCardId };
  const queryParams = createFilteredQueryParams({ perPage: 1, filters });
  const url = '/api/v1/chat_rooms';

  try {
    const data = await queryGet(`${url}?${queryParams.toString()}`);
    return first(data.chatRooms);
  } catch (_error) {
    throw new Error('Error fetching recent chat room.');
  }
}
